import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Building2, Users, TrendingUp, AlertCircle, Activity, Globe, CreditCard } from 'lucide-react';

const OVERDUE_LABELS = {
  subscription_past_due: 'Suscripción con pago atrasado',
  subscription_suspended: 'Suscripción suspendida por cobro',
  payment_failed: 'Último pago fallido',
  period_ended: 'Periodo de facturación vencido',
  renewal_passed: 'Fecha de renovación pasada',
};

function KPICard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-xl p-5 hover:-translate-y-0.5 hover:border-[#CBD5E1] transition-all"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{label}</span>
        <Icon className={`w-5 h-5 ${accent ? 'text-[#F72585]' : 'text-[#64748B]'}`} strokeWidth={1.5} />
      </div>
      <p className="text-3xl font-bold text-[#0F172A] tracking-tight">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/dashboard').then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="admin-dashboard-loading">
        <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] tracking-tight">Dashboard Plataforma</h1>
        <p className="text-sm text-[#64748B] mt-1">Vista general del sistema CavernicolApp</p>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="kpi-strip">
        <KPICard icon={Building2} label="Tenants Activos" value={data?.active_tenants || 0} accent testId="kpi-active-tenants" />
        <KPICard icon={Users} label="Usuarios Total" value={data?.total_users || 0} testId="kpi-total-users" />
        <KPICard icon={TrendingUp} label="Tenants Total" value={data?.total_tenants || 0} testId="kpi-total-tenants" />
        <KPICard icon={AlertCircle} label="Suspendidos" value={data?.suspended_tenants || 0} testId="kpi-suspended" />
        <KPICard icon={CreditCard} label="Pagos Vencidos" value={data?.overdue_tenants_count || 0} accent testId="kpi-overdue" />
      </div>

      {data?.overdue_tenants?.length > 0 && (
        <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6" data-testid="overdue-tenants-panel">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-[#0F172A] tracking-tight">Pagos vencidos</h3>
              <p className="text-sm text-[#64748B] mt-1">
                Clientes con suscripción atrasada o pago fallido. Revisa su plan y contacta al responsable asignado.
              </p>
            </div>
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
              {data.overdue_tenants_count} total
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full bg-white border border-amber-100 rounded-lg">
              <thead>
                <tr className="bg-[#FFFBEB]">
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Cliente</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3 hidden sm:table-cell">Plan</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Motivo</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3 hidden md:table-cell">Días</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Acción</th>
                </tr>
              </thead>
              <tbody>
                {data.overdue_tenants.map((row) => (
                  <tr key={row.tenant_id} className="border-t border-amber-100 hover:bg-[#FFFBEB]/60">
                    <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{row.tenant_name}</td>
                    <td className="px-4 py-3 text-sm text-[#64748B] hidden sm:table-cell capitalize">{row.plan_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-amber-800">{OVERDUE_LABELS[row.overdue_reason] || row.overdue_reason}</td>
                    <td className="px-4 py-3 text-sm text-[#64748B] hidden md:table-cell">{row.overdue_days ?? '—'}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/tenants/${row.tenant_id}`)}
                        className="text-xs font-medium text-[#F72585] hover:underline"
                        data-testid={`overdue-tenant-${row.tenant_id}`}
                      >
                        Ver ficha
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Tenants by vertical */}
        <div className="lg:col-span-8 bg-white border border-[#E2E8F0] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#0F172A] tracking-tight mb-4">Tenants por Vertical</h3>
          {data?.tenants_by_vertical && Object.keys(data.tenants_by_vertical).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(data.tenants_by_vertical).map(([vertical, count]) => (
                <div key={vertical} className="flex items-center justify-between" data-testid={`vertical-stat-${vertical}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#F72585]" />
                    <span className="text-sm text-[#0F172A] capitalize">{vertical.replace(/_/g, ' ')}</span>
                  </div>
                  <span className="text-sm font-semibold text-[#0F172A]">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-[#64748B]">
              <Globe className="w-10 h-10 mb-3 text-[#E2E8F0]" />
              <p className="text-sm">Sin tenants registrados</p>
              <p className="text-xs mt-1">Crea tu primer tenant para comenzar</p>
            </div>
          )}
        </div>

        {/* Recent audit */}
        <div className="lg:col-span-4 bg-white border border-[#E2E8F0] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#0F172A] tracking-tight mb-4">Actividad Reciente</h3>
          {data?.recent_audit_logs?.length > 0 ? (
            <div className="space-y-3">
              {data.recent_audit_logs.slice(0, 8).map((log) => (
                <div key={log.id} className="flex items-start gap-3 text-sm">
                  <Activity className="w-3.5 h-3.5 text-[#F72585] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0F172A] font-medium truncate">{log.action}</p>
                    <p className="text-xs text-[#64748B]">{log.module} &middot; {new Date(log.timestamp_utc).toLocaleString('es-MX')}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#64748B]">Sin actividad reciente</p>
          )}
        </div>
      </div>

      {/* Recent tenants */}
      {data?.recent_tenants?.length > 0 && (
        <div className="mt-6 bg-white border border-[#E2E8F0] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#0F172A] tracking-tight mb-4">Tenants Recientes</h3>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-tenants-table">
              <thead>
                <tr className="bg-[#F8F9FA]">
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Nombre</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Vertical</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Plan</th>
                  <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_tenants.map((t) => (
                  <tr key={t.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{t.name}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 bg-[#F8F9FA] text-[#64748B] rounded-full border border-[#E2E8F0] capitalize">
                        {t.vertical?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#64748B] capitalize">{t.plan}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        t.status === 'active' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                        t.status === 'suspended' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                        'bg-slate-50 text-slate-600 border border-slate-200'
                      }`}>
                        {t.status === 'active' ? 'Activo' : t.status === 'suspended' ? 'Suspendido' : 'Archivado'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
